import React, { Suspense, useEffect, useRef, useState } from 'react';
import {
  Zap, CheckCircle2, ChevronRight, ArrowUpRight, Check, MessageCircle
} from 'lucide-react';
import { useI18n } from '../i18n';
import { landingCopy } from '../i18n/landing';
import { animatePricingCycleSwitch, initLandingPageAnimations } from '../animations/landingGsap';
import { planPriceText, unitCostText, useBillingContext } from '../hooks/useBillingContext';

const ThreeVectorHero = React.lazy(() => import('./ThreeVectorHero'));

interface LandingPageProps {
  onLaunchStudio: () => void;
  onOpenAuth: (mode?: 'login' | 'register') => void;
  onOpenLegal: (page: 'about' | 'terms' | 'privacy') => void;
  onOpenBlog?: () => void;
  theme?: 'dark' | 'light';
}

export const LandingPage: React.FC<LandingPageProps> = ({
  onLaunchStudio,
  onOpenAuth,
  onOpenLegal,
  onOpenBlog,
  theme = 'dark'
}) => {
  const { locale } = useI18n();
  const copy = landingCopy[locale];
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const billingContext = useBillingContext();
  const [enable3dHero, setEnable3dHero] = useState<boolean>(false);
  const landingRootRef = useRef<HTMLElement | null>(null);
  const prevBillingCycleRef = useRef<'monthly' | 'yearly'>('monthly');
  const featureImages = ['/kartu1.webp','/kartu3.webp','/kartu5.webp','/kartu2.webp','/kartu4.webp','/kartu6.webp'];
  const leftFeatures = copy.features.items.slice(0,3).map((item,index)=>({...item,no:['01','03','05'][index],image:featureImages[index]}));
  const rightFeatures = copy.features.items.slice(3).map((item,index)=>({...item,no:['02','04','06'][index],image:featureImages[index+3]}));

  useEffect(() => {
    return initLandingPageAnimations(landingRootRef.current);
  }, []);

  useEffect(() => {
    const prevCycle = prevBillingCycleRef.current;
    if (prevCycle !== billingCycle) {
      prevBillingCycleRef.current = billingCycle;
      const tween = animatePricingCycleSwitch(landingRootRef.current, prevCycle, billingCycle);
      return () => {
        tween?.kill();
      };
    }
    return undefined;
  }, [billingCycle]);

  useEffect(() => {
    // Only mount heavy WebGL on desktop devices and after main thread is idle
    if (typeof window !== 'undefined' && window.innerWidth >= 768) {
      if ('requestIdleCallback' in window) {
        const id = (window as any).requestIdleCallback(() => setEnable3dHero(true), { timeout: 1200 });
        return () => (window as any).cancelIdleCallback(id);
      } else {
        const timer = setTimeout(() => setEnable3dHero(true), 200);
        return () => clearTimeout(timer);
      }
    }
  }, []);

  useEffect(() => {
    const origin = window.location.origin;
    const structuredData = {
      '@context': 'https://schema.org',
      '@graph': [
        { '@type': 'Organization', '@id': `${origin}/#organization`, name: 'Vectria', url: origin, logo: `${origin}/logo_original.webp` },
        { '@type': 'WebApplication', '@id': `${origin}/#webapp`, name: 'Vectria', url: `${origin}${window.location.pathname}`, applicationCategory: 'DesignApplication', operatingSystem: 'Web', description: copy.hero.subtitle, offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' }, publisher: { '@id': `${origin}/#organization` } },
        { '@type': 'FAQPage', mainEntity: copy.faq.items.map(item => ({ '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } })) }
      ]
    };
    const staticScript = document.getElementById('vectria-schema');
    if (staticScript) {
      staticScript.remove();
    }
    let script = document.head.querySelector<HTMLScriptElement>('script[data-vectria-structured-data]');
    if (!script) { script = document.createElement('script'); script.type = 'application/ld+json'; script.dataset.vectriaStructuredData = 'true'; document.head.appendChild(script); }
    script.textContent = JSON.stringify(structuredData);
    return () => { script?.remove(); };
  }, [locale, copy]);

  const toggleFaq = (index: number) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  return (
    <main ref={landingRootRef} style={{ minHeight: '100dvh', background: 'var(--md-sys-color-background)', color: 'var(--md-sys-color-on-background)', position: 'relative', overflowX: 'hidden' }}>
      {/* 2. Hero Section with Three.js 3D Background */}
      <section className="landing-art-section landing-hero-section" style={{ position: 'relative', minHeight: '90dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 24px 80px', overflow: 'hidden' }}>
        {/* Three.js loads as a separate WebGL chunk on desktop when idle, saving mobile TBT */}
        {enable3dHero ? (
          <Suspense fallback={<div aria-hidden="true" className="landing-hero-fallback" />}>
            <ThreeVectorHero theme={theme} />
          </Suspense>
        ) : (
          <div aria-hidden="true" className="landing-hero-fallback" />
        )}

        {/* Restrained contrast veil keeps the product render behind the message. */}
        <div className="landing-hero-veil" aria-hidden="true" />

        <div className="landing-hero-content" style={{ position: 'relative', zIndex: 10, maxWidth: '960px', width: '100%', textAlign: 'center', margin: '0 auto' }}>
          {/* Headline */}
          <h1 className="landing-hero-title" style={{ fontSize: 'clamp(36px, 6vw, 64px)', fontWeight: 800, lineHeight: 1.12, letterSpacing: '-0.03em', marginBottom: '24px' }}>
            {copy.hero.lead}{' '}
            <span className="landing-hero-accent">{copy.hero.accent}</span>
          </h1>

          {/* Subtitle */}
          <p className="landing-hero-subtitle" style={{ fontSize: 'clamp(16px, 2vw, 20px)', lineHeight: 1.6, color: 'var(--md-sys-color-on-surface-variant)', maxWidth: '780px', margin: '0 auto 36px', fontWeight: 400 }}>
            {copy.hero.subtitle}
          </p>

          {/* Hero CTAs */}
          <div className="landing-hero-actions" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '40px' }}>
            <button
              onClick={() => onOpenAuth('register')}
              className="m3-btn m3-btn-filled m3-btn-lg landing-primary-cta"
            >
              <Zap size={18} fill="currentColor" />
              <span>{copy.hero.free}</span>
            </button>

            <button
              onClick={onLaunchStudio}
              className="m3-btn m3-btn-tonal m3-btn-lg landing-secondary-cta"
            >
              <span>{copy.hero.demo}</span>
              <ArrowUpRight size={18} />
            </button>
          </div>

          {/* Micro-Trust Pills */}
          <div className="landing-hero-trust" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '24px', flexWrap: 'wrap', color: 'var(--md-sys-color-on-surface-variant)', fontSize: '13px' }}>
            {copy.hero.trust.map(item=><div key={item} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><CheckCircle2 size={16} color="var(--palette-38ef7d)" /><span>{item}</span></div>)}
          </div>
        </div>
      </section>

      {/* 3. Engine Capabilities — Connected Architecture Showcase */}
      <section id="features" className="capabilities-showcase">
        <div className="capabilities-heading">
          <span className="capabilities-eyebrow">{copy.features.eyebrow}</span>
          <h2>{copy.features.lead}<br />{copy.features.accent} {copy.features.tail}</h2>
          <p>{copy.features.subtitle}</p>
        </div>

        <div className="capabilities-map">
          <svg className="capabilities-connectors" viewBox="0 0 1200 390" preserveAspectRatio="none" aria-hidden="true">
            <defs>
              <linearGradient id="connectorLeft" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="var(--md-sys-color-outline)" stopOpacity="0.3" />
                <stop offset="100%" stopColor="var(--md-sys-color-primary)" stopOpacity="0.65" />
              </linearGradient>
              <linearGradient id="connectorRight" x1="1" y1="0" x2="0" y2="0">
                <stop offset="0%" stopColor="var(--md-sys-color-outline)" stopOpacity="0.3" />
                <stop offset="100%" stopColor="var(--md-sys-color-primary)" stopOpacity="0.65" />
              </linearGradient>
            </defs>
            <g className="connector-paths connector-paths-left">
              <path d="M 400 57 C 455 57, 458 104, 520 122" />
              <path className="connector-middle" d="M 400 195 C 448 195, 474 181, 518 195" />
              <path d="M 400 333 C 455 333, 458 286, 520 268" />
            </g>
            <g className="connector-paths connector-paths-right">
              <path d="M 800 57 C 745 57, 742 104, 680 122" />
              <path className="connector-middle" d="M 800 195 C 752 195, 726 181, 682 195" />
              <path d="M 800 333 C 745 333, 742 286, 680 268" />
            </g>
            <g className="connector-pulses" aria-hidden="true">
              <path d="M 400 57 C 455 57, 458 104, 520 122" />
              <path d="M 400 195 C 448 195, 474 181, 518 195" />
              <path d="M 400 333 C 455 333, 458 286, 520 268" />
              <path d="M 800 57 C 745 57, 742 104, 680 122" />
              <path d="M 800 195 C 752 195, 726 181, 682 195" />
              <path d="M 800 333 C 745 333, 742 286, 680 268" />
            </g>
            <g className="connector-nodes">
              <circle className="connector-node-card" cx="400" cy="57" r="4" />
              <circle className="connector-node-core" cx="520" cy="122" r="4" />
              <circle className="connector-node-card" cx="400" cy="195" r="4" />
              <circle className="connector-node-core" cx="518" cy="195" r="4" />
              <circle className="connector-node-card" cx="400" cy="333" r="4" />
              <circle className="connector-node-core" cx="520" cy="268" r="4" />
              <circle className="connector-node-card" cx="800" cy="57" r="4" />
              <circle className="connector-node-core" cx="680" cy="122" r="4" />
              <circle className="connector-node-card" cx="800" cy="195" r="4" />
              <circle className="connector-node-core" cx="682" cy="195" r="4" />
              <circle className="connector-node-card" cx="800" cy="333" r="4" />
              <circle className="connector-node-core" cx="680" cy="268" r="4" />
            </g>
          </svg>

          <div className="capabilities-column capabilities-column-left">
            {leftFeatures.map((feature, index) => (
              <article className={`capability-card capability-left capability-tone-${index + 1}`} key={feature.no}>
                <div className="capability-icon capability-image">
                  <img src={feature.image} alt={`${feature.title} illustration`} width="72" height="72" loading="lazy" decoding="async" />
                </div>
                <div><span className="capability-number">{feature.no}</span><h3>{feature.title}</h3><p>{feature.text}</p></div>
                <i className="capability-connector" aria-hidden="true" />
              </article>
            ))}
          </div>

          <div className="capabilities-core" aria-label="Vectria layered vector processing architecture">
            <img className="capabilities-core-image" src="/uiglass.webp" alt="Vectria glass UI processing architecture" width="480" height="480" loading="lazy" decoding="async" />
          </div>

          <div className="capabilities-column capabilities-column-right">
            {rightFeatures.map((feature, index) => (
              <article className={`capability-card capability-right capability-tone-${index + 4}`} key={feature.no}>
                <i className="capability-connector" aria-hidden="true" />
                <div className="capability-icon capability-image">
                  <img src={feature.image} alt={`${feature.title} illustration`} width="72" height="72" loading="lazy" decoding="async" />
                </div>
                <div><span className="capability-number">{feature.no}</span><h3>{feature.title}</h3><p>{feature.text}</p></div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Pricing Section (Global SaaS Ready) */}
      <section id="pricing" className="pricing-section">
        <div className="pricing-container">
          <div className="pricing-header">
            <span className="pricing-eyebrow">{copy.pricing.eyebrow}</span>
            <h2 className="pricing-title">{copy.pricing.title}</h2>
            <p className="pricing-subtitle">{copy.pricing.subtitle}</p>

            {/* Refined Billing Switcher */}
            <div className="pricing-toggle-wrap">
              <button
                type="button"
                className={`pricing-toggle-btn ${billingCycle === 'monthly' ? 'is-active' : ''}`}
                onClick={() => setBillingCycle('monthly')}
              >
                {copy.pricing.monthly}
              </button>
              <button
                type="button"
                className={`pricing-toggle-btn ${billingCycle === 'yearly' ? 'is-active' : ''}`}
                onClick={() => setBillingCycle('yearly')}
              >
                <span>{copy.pricing.annual}</span>
                <span className="pricing-save-chip">{copy.pricing.save}</span>
              </button>
            </div>
          </div>

          {/* 3-Plan Cards Grid */}
          <div className="pricing-cards-grid">
            {/* 1. Starter Plan */}
            <article className="pricing-card pricing-card-standard">
              <div className="pricing-card-header">
                <span className="plan-name">{copy.pricing.plans[0].name}</span>
                <p className="plan-desc">{copy.pricing.plans[0].desc}</p>
                <div className="plan-price">
                  <span className="price-num" data-gsap-price="starter">{planPriceText(billingContext, 'starter', billingCycle).text}</span>
                  <span className="price-cycle">{billingCycle === 'monthly' ? copy.pricing.month : copy.pricing.year}</span>
                </div>
                <div className="plan-allowance">
                  <span className="plan-allowance-line"><strong data-gsap-credits="starter-vector">{billingCycle === 'monthly' ? '200' : '2,400'}</strong> {copy.pricing.vectorCredits} {billingCycle === 'monthly' ? copy.pricing.month : copy.pricing.year} <span className="unit-cost">{unitCostText(billingContext, 'starter', billingCycle, copy.pricing.perCredit)}</span></span>
                  <span className="plan-allowance-line"><strong>0</strong> AI Credits {billingCycle === 'monthly' ? copy.pricing.month : copy.pricing.year} · BYOK</span>
                </div>
              </div>

              <ul className="plan-features">{copy.pricing.plans[0].features.map(item=><li key={item}><Check size={16} className="feature-check"/><span>{item}</span></li>)}</ul>

              <div className="plan-cta-wrap">
                <button
                  type="button"
                  onClick={() => onOpenAuth('register')}
                  className="m3-btn m3-btn-tonal m3-btn-md plan-cta-btn"
                >
                  <span>{copy.pricing.plans[0].cta}</span>
                </button>
              </div>
            </article>

            {/* 2. Pro Plan (Single Featured Option) */}
            <article className="pricing-card pricing-card-featured">
              <div className="featured-badge-wrap">
                <span className="featured-badge">{copy.pricing.popular}</span>
              </div>

              <div className="pricing-card-header">
                <span className="plan-name">{copy.pricing.plans[1].name}</span>
                <p className="plan-desc">{copy.pricing.plans[1].desc}</p>
                <div className="plan-price">
                  <span className="price-num" data-gsap-price="pro">{planPriceText(billingContext, 'pro', billingCycle).text}</span>
                  <span className="price-cycle">{billingCycle === 'monthly' ? copy.pricing.month : copy.pricing.year}</span>
                </div>
                <div className="plan-allowance plan-allowance-pro">
                  <span className="plan-allowance-line"><strong data-gsap-credits="pro-vector">{billingCycle === 'monthly' ? '500' : '6,000'}</strong> {copy.pricing.vectorCredits} {billingCycle === 'monthly' ? copy.pricing.month : copy.pricing.year} <span className="unit-cost">{unitCostText(billingContext, 'pro', billingCycle, copy.pricing.perCredit)}</span></span>
                  <span className="plan-allowance-line"><strong data-gsap-credits="pro-ai">{billingCycle === 'monthly' ? '500' : '6,000'}</strong> AI Credits {billingCycle === 'monthly' ? copy.pricing.month : copy.pricing.year}</span>
                </div>
              </div>

              <ul className="plan-features">{copy.pricing.plans[1].features.map(item=><li key={item}><Check size={16} className="feature-check feature-check-pro"/><span>{item}</span></li>)}</ul>

              <div className="plan-cta-wrap">
                <button
                  type="button"
                  onClick={() => onOpenAuth('register')}
                  className="m3-btn m3-btn-filled m3-btn-md plan-cta-btn plan-cta-btn-featured"
                >
                  <span>{copy.pricing.plans[1].cta}</span>
                </button>
              </div>
            </article>

            {/* 3. Studio Plan */}
            <article className="pricing-card pricing-card-standard">
              <div className="pricing-card-header">
                <span className="plan-name">{copy.pricing.plans[2].name}</span>
                <p className="plan-desc">{copy.pricing.plans[2].desc}</p>
                <div className="plan-price">
                  <span className="price-num" data-gsap-price="studio">{planPriceText(billingContext, 'studio', billingCycle).text}</span>
                  <span className="price-cycle">{billingCycle === 'monthly' ? copy.pricing.month : copy.pricing.year}</span>
                </div>
                <div className="plan-allowance">
                  <span className="plan-allowance-line"><strong data-gsap-credits="studio-vector">{billingCycle === 'monthly' ? '1,500' : '18,000'}</strong> {copy.pricing.vectorCredits} {billingCycle === 'monthly' ? copy.pricing.month : copy.pricing.year} <span className="unit-cost">{unitCostText(billingContext, 'studio', billingCycle, copy.pricing.perCredit)}</span></span>
                  <span className="plan-allowance-line"><strong data-gsap-credits="studio-ai">{billingCycle === 'monthly' ? '1,500' : '18,000'}</strong> AI Credits {billingCycle === 'monthly' ? copy.pricing.month : copy.pricing.year}</span>
                </div>
              </div>

              <ul className="plan-features">{copy.pricing.plans[2].features.map(item=><li key={item}><Check size={16} className="feature-check"/><span>{item}</span></li>)}</ul>

              <div className="plan-cta-wrap">
                <button
                  type="button"
                  onClick={() => onOpenAuth('register')}
                  className="m3-btn m3-btn-tonal m3-btn-md plan-cta-btn"
                >
                  <span>{copy.pricing.plans[2].cta}</span>
                </button>
              </div>
            </article>
          </div>

          {/* Billing Disclosures */}
          <div className="pricing-disclosures">
            <span className="pricing-note">
              {copy.pricing.note}
            </span>
            {billingContext.one_time_period && copy.pricing.oneTime ? (
              <span className="pricing-note pricing-note-one-time">{copy.pricing.oneTime}</span>
            ) : null}
            {billingContext.currency === 'IDR' && copy.pricing.fxNote ? (
              <span className="pricing-note pricing-note-fx">
                {copy.pricing.fxNote}
                {billingContext.fx_attribution ? ` ${billingContext.fx_attribution}.` : ''}
              </span>
            ) : null}
          </div>
        </div>
      </section>

      {/* 5. Frequently Asked Questions — Editorial Split */}
      <section id="faq" className="faq-editorial">
        <div className="faq-editorial-grid">
          <div className="faq-intro">
            <span className="faq-eyebrow">{copy.faq.eyebrow}</span>
            <h2>{copy.faq.lead}<br /><span>{copy.faq.accent}</span></h2>
            <p>{copy.faq.subtitle}</p>

            <aside className="faq-support-panel">
              <div className="faq-support-icon"><MessageCircle size={19} /></div>
              <div>
                <h3>{copy.faq.supportTitle}</h3>
                <p>{copy.faq.supportText}</p>
                <a href="mailto:support@vectria.app">{copy.faq.contact} <ArrowUpRight size={13} /></a>
              </div>
            </aside>
          </div>

          <div className="faq-accordion">
            {copy.faq.items.map((item, idx) => {
              const isOpen = activeFaq === idx;
              const triggerId = `faq-trigger-${idx}`;
              const panelId = `faq-panel-${idx}`;
              return (
                <article className={`faq-item ${isOpen ? 'is-open' : ''}`} key={item.q}>
                  <h3>
                    <button
                      id={triggerId}
                      type="button"
                      aria-expanded={isOpen}
                      aria-controls={panelId}
                      onClick={() => toggleFaq(idx)}
                    >
                      <span>{item.q}</span>
                      <span className="faq-toggle-icon" aria-hidden="true"><span /><span /></span>
                    </button>
                  </h3>
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={triggerId}
                    className="faq-answer-wrap"
                    aria-hidden={!isOpen}
                  >
                    <div className="faq-answer"><p>{item.a}</p></div>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* 8. Bottom CTA Banner */}
      <section className="landing-bottom-cta">
        <div className="landing-bottom-cta-inner">
          <h2>{copy.cta.title}</h2>
          <p>{copy.cta.text}</p>
          <button
            onClick={() => onOpenAuth('register')}
            className="m3-btn m3-btn-filled m3-btn-lg landing-primary-cta"
          >
            <span>{copy.cta.button}</span>
            <ChevronRight size={18} />
          </button>
        </div>
      </section>

      {/* 9. Global Footer */}
      <footer style={{ padding: '40px 24px', borderTop: '1px solid var(--md-sys-color-outline-variant)', background: theme === 'light' ? 'var(--color-white)' : 'var(--palette-03050c)', fontSize: '13px', color: 'var(--md-sys-color-on-surface-variant)' }}>
        <div style={{ maxWidth: '1120px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img src="/emblem.webp" alt="Vectria Emblem" width="22" height="22" loading="lazy" decoding="async" style={{ height: '22px', width: 'auto' }} />
            <span className="brand-wordmark brand-wordmark-footer">VECTRIA</span>
            <span className="footer-tagline">{copy.footer.tagline}</span>
          </div>

          <div className="footer-producthunt-badge" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <a
              href="https://www.producthunt.com/products/vectria?embed=true&utm_source=badge-featured&utm_medium=badge&utm_campaign=badge-vectria"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', maxWidth: '100%' }}
            >
              <img
                src={`https://api.producthunt.com/widgets/embed-image/v1/featured.svg?post_id=1295652&theme=${theme === 'light' ? 'light' : 'dark'}&t=1793520633726`}
                alt="Vectria - Turn raster images into clean, editable SVG & EPS vectors | Product Hunt"
                width={250}
                height={54}
                loading="lazy"
                decoding="async"
                style={{ width: '250px', maxWidth: '100%', height: 'auto', aspectRatio: '250 / 54', display: 'block' }}
              />
            </a>
          </div>

          <a
            href="https://www.codehype.ai/product/vectria?utm_source=codehype_badge"
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: 'inline-block', border: 0, width: '100%', maxWidth: '180px', height: 'auto', maxHeight: '65px' }}
          >
            <img
              src="https://www.codehype.ai/badges/vectria.svg?variant=find-us&v=20"
              alt="Featured on CodeHype"
              width={180}
              height={65}
              loading="lazy"
              decoding="async"
              style={{ display: 'block', border: 0, width: '100%', maxWidth: '180px', height: 'auto', maxHeight: '65px', aspectRatio: '180 / 65' }}
            />
          </a>

          <div style={{ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap' }}>
            <button className="footer-legal-link" onClick={() => onOpenLegal('about')}>{copy.footer.about}</button>
            <button className="footer-legal-link" onClick={() => onOpenLegal('terms')}>{copy.footer.terms}</button>
            <button className="footer-legal-link" onClick={() => onOpenLegal('privacy')}>{copy.footer.privacy}</button>
            {onOpenBlog && <button className="footer-legal-link" onClick={onOpenBlog}>Blog &amp; Guides</button>}
            <span>© {new Date().getFullYear()} Vectria. {copy.footer.rights}</span>
          </div>
        </div>
      </footer>
    </main>
  );
};
export default LandingPage;
